/**
 * Shared knowledge-base contracts for Aria support AI.
 *
 * The KB is the assistant's "support brain": curated, human-maintained facts about how the product
 * works (feature how-tos, by-design limitations) and structured troubleshooting playbooks. It is the
 * ONLY source the assistant may use to answer support questions — the assistant never invents product
 * behavior. Entries live in `pure-js/libs/assistantKnowledgeBase.ts` and are compiled into the backend
 * KB module (`functions/src/services/assistant/assistantKnowledge.ts`).
 *
 * Every field on `KnowledgeEntry` is compiled into the grounding block by `buildKnowledgeGrounding` —
 * do not add a field the grounding does not emit, or its content silently never reaches the model.
 */

import type { AssistantRouteKey } from '../libs/assistantNavigation'

export enum AssistantAudience {
  GAMER = 'gamer',
  FACILITATOR = 'facilitator',
  /** A participant who also leads people: sees everything written for gamers plus the leader entries. */
  LEADER = 'leader',
  /**
   * On an entry: relevant to every role. As a *request* it means "role unknown" and therefore returns
   * only `ALL`-tagged entries — an unresolved role must never widen access to role-specific content.
   */
  ALL = 'all'
}

export enum AssistantKnowledgeCategory {
  /** How a feature works / how to use it. */
  FEATURE = 'feature',
  /** "I can't do X" — intended behavior, not a bug. */
  BY_DESIGN = 'by_design',
  /** Step-by-step fixes for a reported symptom. */
  TROUBLESHOOTING = 'troubleshooting',
  /** Where to find something in the app. */
  NAVIGATION = 'navigation'
}

/**
 * Read-only diagnostics the assistant is allowed to run. These double as the realtime tool whitelist:
 * the assistant can only ever request one of these (plus navigation + KB lookup) — never a write.
 * Single source of truth shared by the KB, the live-token tool declarations, and the client dispatcher.
 */
export enum AssistantDiagnosticKind {
  REFRESH_PAGE = 'refresh_page',
  CHECK_MICROPHONE = 'check_microphone',
  CHECK_CAMERA = 'check_camera',
  CHECK_CONNECTION = 'check_connection',
  CHECK_WHEREBY_ROOM = 'check_whereby_room',
  CHECK_ANALYSIS_STATUS = 'check_analysis_status',
  CHECK_FEEDBACK_READY = 'check_feedback_ready',
  CHECK_UPCOMING_ACTIVITY = 'check_upcoming_activity'
}

/** Derived from the enum so the list and the type can never drift. */
export const ASSISTANT_DIAGNOSTIC_KINDS: AssistantDiagnosticKind[] = Object.values(AssistantDiagnosticKind)

export type TroubleshootingStep = {
  /** Plain-language instruction shown to / spoken by the assistant. */
  instruction: string
  /** Optional read-only diagnostic the assistant may run to verify or auto-resolve this step. */
  diagnostic?: AssistantDiagnosticKind
}

export type KnowledgeEntry = {
  id: string
  title: string
  audience: AssistantAudience
  category: AssistantKnowledgeCategory
  /** The whole answer, in 1–2 sentences. This is the only prose fed to the model — keep it tight. */
  summary: string
  /** Route keys the assistant may offer to navigate to; compile-checked against the allowlist. */
  relatedRoutes?: AssistantRouteKey[]
  /** Ordered remediation steps when this entry is a fix for a symptom. */
  troubleshooting?: TroubleshootingStep[]
  /**
   * Only ground the model with this when the user actually belongs to more than one organization.
   * Telling a single-org user about a switcher they do not have sends them looking for a control that
   * is not there, and no amount of "do not mention it" outranks an entry sitting in the knowledge.
   */
  requiresMultipleOrganizations?: boolean
}
